import { useEffect } from "react";
import type { FC } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { useTypedSelector } from "../redux/store";

import {
  updateDialogue,
  removeMessageFromDialogue
} from "../redux/slices/privateChat";

import {
  addGlobalMessage,
  removeGlobalMessage,
  setUnseenChats
} from "../redux/slices/globalChat";

import type {
  DialogueResult,
  MessageResult
} from "../api/generated/data-contracts";
import { Outlet, useParams } from "react-router-dom";

import { queryClient } from "..";
import { API_URL } from "../api";
import { createSignalRContext } from "react-signalr";

const GlobalChatSRContext = createSignalRContext();
const PrivateChatSRContext = createSignalRContext();

export const ChatsSRProvider: FC = () => {
  const dispatch = useDispatch();
  const userJwt = useTypedSelector((state) => state.auth.value);

  const params = useParams<{ interlocutorId: string }>();

  const isPrivateChatLoading = useTypedSelector(
    (state) => state.privateChat.isLoading
  );
  const isGlobalChatLoading = useTypedSelector(
    (state) => state.globalChat.isLoading
  );

  // Private chat events
  PrivateChatSRContext.useSignalREffect(
    "ReceivePrivateMessage",
    (dialogue: DialogueResult) => {
      console.log("ReceivePrivateMessage");

      queryClient.invalidateQueries({
        queryKey: [`getDialogMessages`, params.interlocutorId]
      });
      dispatch(setUnseenChats((prev) => prev + 1));
      dispatch(updateDialogue(dialogue));
    },
    [dispatch]
  );

  PrivateChatSRContext.useSignalREffect(
    "PrivateMessageDeleted",
    (message: MessageResult) => {
      console.log("PrivateMessageDeleted");
      queryClient.invalidateQueries({
        queryKey: [`getDialogMessages`, params.interlocutorId]
      });
      dispatch(removeMessageFromDialogue({ messageId: message.id }));
    },
    [dispatch]
  );

  // Global chat events
  GlobalChatSRContext.useSignalREffect(
    "ReceiveGlobalMessage",
    (message) => {
      console.log("ReceiveGlobalMessage");
      queryClient.invalidateQueries({
        queryKey: [`getAllGlobalMessages`]
      });
      dispatch(setUnseenChats((prev) => prev + 1));
      dispatch(addGlobalMessage(message));
    },
    [dispatch]
  );

  GlobalChatSRContext.useSignalREffect(
    "GlobalMessageDeleted",
    (message) => {
      console.log("GlobalMessageDeleted");
      queryClient.invalidateQueries({
        queryKey: [`getAllGlobalMessages`]
      });
      dispatch(removeGlobalMessage(message.id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (isPrivateChatLoading) {
      toast.info("Loading private chat messages...", {
        toastId: "privateChat-loading"
      });
    }
    if (isGlobalChatLoading) {
      toast.info("Loading global chat messages...", {
        toastId: "globalChat-loading"
      });
    }
  }, [isPrivateChatLoading, isGlobalChatLoading]);

  return (
    <GlobalChatSRContext.Provider
      url={`${API_URL}/hubs/globalChat`}
      onReconnect={(a) => {
        console.log("onReconnect", a);
      }}
      onError={async (a) =>
        new Promise<void>((resolve) => {
          console.log("onError", a);
        })
      }
      onBeforeClose={(a) => {
        console.log("onBeforeClose", a);
      }}
    >
      <PrivateChatSRContext.Provider
        accessTokenFactory={() => userJwt}
        dependencies={[userJwt]}
        connectEnabled={!!userJwt}
        url={`${API_URL}/hubs/privateChat`}
        onReconnect={(a) => {
          console.log("onReconnect", a);
        }}
        onError={async (a) =>
          new Promise<void>((resolve) => {
            console.log("onError", a);
          })
        }
        onBeforeClose={(a) => {
          console.log("onBeforeClose", a);
        }}
      >
        <Outlet />
      </PrivateChatSRContext.Provider>
    </GlobalChatSRContext.Provider>
  );
};
